/**
 * Shell-level keys: navigation, mobile header, music picker, push prompt,
 * chat FAB, update banner, and the language switcher itself.
 *
 * Convention reminders for everyone adding keys to other modules:
 *  - Flat dotted keys grouped by feature (`shop.title`, `quiz.hint.label`).
 *  - Variables are `{name}` placeholders, interpolated by t().
 *  - Both zh and en are required; missing en silently falls back to zh.
 *  - Keep emojis inside the text where they're cosmetic.
 */
const shell = {
  zh: {
    'common.save': '保存',
    'common.cancel': '取消',
    'common.confirm': '确定',
    'common.delete': '删除',
    'common.edit': '编辑',
    'common.back': '返回',
    'common.close': '关闭',
    // v0.11.0 — TopBar a11y labels
    'topbar.aria.wallet': '打开钱包',
    'topbar.aria.bond':   '查看亲密度档案',
    'topbar.aria.settings': '设置',
    'topbar.keeper.rank': 'Lv.{level} 园长 · {title}',
    'topbar.pet.bondLvl': '亲密度 Lv.{level}',
    'pwa.hint.title': '把小陪伴装到主屏幕，体验更顺畅',
    'pwa.hint.chromium.body': '一键添加到主屏幕，下次直接打开，不用走浏览器，速度更快～',
    'pwa.hint.chromium.cta': '安装到主屏幕',
    'pwa.hint.ios.line1': '👉 点 Safari 底部「分享」按钮 ⬆️',
    'pwa.hint.ios.line2': '再选「添加到主屏幕」就能像 App 一样打开啦',
    'pwa.hint.other.body': '建议在浏览器菜单选「添加到主屏幕 / 安装应用」，下次一键打开。',
    'pwa.hint.kid.title': '把宠物装到主屏幕，下次一秒打开 🐹',
    'pwa.hint.kid.chromium.body': '点下面的按钮就行，宠物会变成一个小图标住在你的桌面上！',
    'pwa.hint.kid.ios.step1': '点屏幕底部的「分享」按钮 ⬆️',
    'pwa.hint.kid.ios.step2': '在弹出来的菜单里找到「添加到主屏幕」',
    'pwa.hint.kid.ios.step3': '点右上角「添加」就好啦，回到桌面就能看到 🎉',
    'pwa.hint.kid.other.body': '在浏览器菜单里选「添加到主屏幕」，宠物就能像 App 一样住在你的桌面～',
    'pwa.hint.kid.later': '稍后再说',
    'pwa.hint.kid.neverAgain': '不用了',
    'common.retry': '重试',
    'common.loading': '加载中…',
    'common.empty': '暂时还没有内容',
    'common.error': '出错了',
    'common.yes': '是',
    'common.no': '否',
    'common.ok': '好的',
    'common.continue': '继续',
    'common.next': '下一步',
    'common.prev': '上一步',
    'common.skip': '跳过',
    'common.done': '完成',
    'common.send': '发送',
    'common.copy': '复制',
    'common.copied': '已复制',
    'common.search': '搜索',

    'lang.switch.aria': '切换语言',
    'lang.switch.locked.tooltip': '语言已被家长锁定',
    'lang.zh.short': '中',
    'lang.en.short': 'EN',
    'lang.zh.full': '中文',
    'lang.en.full': 'English',

    'nav.home.label': '我的乐园',
    'nav.home.helper': '园长主场',
    'nav.timeline.label': '我们的故事',
    'nav.timeline.helper': '纪念日时间线',
    'nav.chat.label': '伙伴聊天',
    'nav.chat.helper': '和 AI 宠物对话',
    'nav.earn.header': '赚金币',
    'nav.quiz.label': '智慧森林',
    'nav.quiz.helper': '帮宠物解谜',
    'nav.games.label': '小游戏',
    'nav.games.helper': '速算小挑战',
    'nav.habits.label': '习惯养成',
    'nav.habits.helper': '小任务',
    'nav.imagine.label': '无限遐想',
    'nav.imagine.helper': '写你的世界',
    'nav.zoo.label': '动物乐园',
    'nav.zoo.helper': '乐园 + 繁育',
    'nav.shop.label': '宠物店',
    'nav.shop.helper': '领养补给',
    'nav.wallet.label': '💰 钱包',
    'nav.wallet.go': '去钱包',
    'nav.more.label': '更多',
    'nav.more.aria': '更多入口',

    'header.title.home': '我的动物乐园',
    'header.title.zoo': '动物乐园',
    'header.title.wallet': '钱包',
    'header.title.shop': '商店',
    'header.title.chat': '伙伴聊天',
    'header.title.quiz': '答题挑战',
    'header.title.imagine': '想象世界',
    'header.title.me': '我的档案',
    'header.title.timeline': '我们的故事',
    'header.title.fallback': '我的动物乐园',

    'header.pet.waiting': '{name} 在等你',
    'header.pet.fallback': '小伙伴',
    'header.profile.aria': '{name} 的档案',
    'header.home.aria': '回到家园',
    'header.wallet.aria': '查看钱包',

    'nav.brand.rank': '{emoji} Lv.{level} · {title}',

    'music.title': '背景音乐',
    'music.aria': '背景音乐',
    'music.tooltip.on': '背景音乐：{style}',
    'music.tooltip.off': '背景音乐已关闭',
    'music.power.on': '音乐已开启',
    'music.power.off': '音乐已关闭',
    'music.power.helper.on': '点一下关闭背景音乐',
    'music.power.helper.off': '点一下打开背景音乐',
    'music.foot.scene': '场景声音始终保留',

    'push.aria': '打开 iPad 系统聊天提醒',
    'push.title.denied': '系统通知被拒绝，请到 iPad 设置里允许',
    'push.title.prompt': '打开聊天提醒',
    'push.label.denied': '提醒被关',
    'push.label.prompt': '打开提醒',

    'chat.fab.unread.aria': '{name} 找你聊天',
    'chat.fab.idle.aria': '和 {name} 聊聊',
    'chat.fab.unread.label': '回信',
    'chat.fab.idle.label': '聊聊',

    'update.banner.title': '有新版本啦',
    'update.banner.body': '点一下立即更新到最新版',
    'update.banner.button': '立即更新',
    'update.banner.dismiss': '稍后',
  },
  en: {
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.confirm': 'OK',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.back': 'Back',
    'common.close': 'Close',
    // v0.11.0 — TopBar a11y labels
    'topbar.aria.wallet': 'Open wallet',
    'topbar.aria.bond':   'View bond profile',
    'topbar.aria.settings': 'Settings',
    'topbar.keeper.rank': 'Keeper Lv.{level} · {title}',
    'topbar.pet.bondLvl': 'Bond Lv.{level}',
    'pwa.hint.title': 'Add Little Companion to your home screen for the best feel',
    'pwa.hint.chromium.body': "One tap and it'll behave like a real app — faster to open, no browser bars.",
    'pwa.hint.chromium.cta': 'Install to home screen',
    'pwa.hint.ios.line1': '👉 Tap the Safari Share button at the bottom ⬆️',
    'pwa.hint.ios.line2': 'Then choose "Add to Home Screen" — opens like an app from then on.',
    'pwa.hint.other.body': "In your browser's menu, pick \"Add to home screen\" or \"Install app\" so you can launch it in one tap.",
    'pwa.hint.kid.title': 'Put your pet on your home screen — opens in one second 🐹',
    'pwa.hint.kid.chromium.body': "Tap the button below — your pet becomes a tiny icon that lives on your home screen!",
    'pwa.hint.kid.ios.step1': 'Tap the Share button at the bottom ⬆️',
    'pwa.hint.kid.ios.step2': 'In the menu, find "Add to Home Screen"',
    'pwa.hint.kid.ios.step3': 'Tap "Add" in the corner — go home and you\'ll see it 🎉',
    'pwa.hint.kid.other.body': "In your browser menu, pick \"Add to home screen\" — your pet will live on your home screen like an app.",
    'pwa.hint.kid.later': 'Maybe later',
    'pwa.hint.kid.neverAgain': "Don't ask again",
    'common.retry': 'Try again',
    'common.loading': 'Loading…',
    'common.empty': 'Nothing here yet',
    'common.error': 'Something went wrong',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.ok': 'OK',
    'common.continue': 'Continue',
    'common.next': 'Next',
    'common.prev': 'Back',
    'common.skip': 'Skip',
    'common.done': 'Done',
    'common.send': 'Send',
    'common.copy': 'Copy',
    'common.copied': 'Copied',
    'common.search': 'Search',

    'lang.switch.aria': 'Switch language',
    'lang.switch.locked.tooltip': 'Language is locked by your parent',
    'lang.zh.short': '中',
    'lang.en.short': 'EN',
    'lang.zh.full': 'Chinese',
    'lang.en.full': 'English',

    'nav.home.label': 'My Park',
    'nav.home.helper': 'Keeper home',
    'nav.timeline.label': 'Our story',
    'nav.timeline.helper': 'Memory timeline',
    'nav.chat.label': 'Chat',
    'nav.chat.helper': 'Talk with your AI pet',
    'nav.earn.header': 'Earn coins',
    'nav.quiz.label': 'Wisdom Forest',
    'nav.quiz.helper': 'Help your pet solve puzzles',
    'nav.games.label': 'Mini-games',
    'nav.games.helper': 'Quick brain sprints',
    'nav.habits.label': 'Good habits',
    'nav.habits.helper': 'Small tasks',
    'nav.imagine.label': 'Imagination',
    'nav.imagine.helper': 'Write your world',
    'nav.zoo.label': 'Animal park',
    'nav.zoo.helper': 'Park + breeding',
    'nav.shop.label': 'Pet shop',
    'nav.shop.helper': 'Adopt & supplies',
    'nav.wallet.label': '💰 Wallet',
    'nav.wallet.go': 'Open wallet',
    'nav.more.label': 'More',
    'nav.more.aria': 'More options',

    'header.title.home': 'My Animal Park',
    'header.title.zoo': 'Animal park',
    'header.title.wallet': 'Wallet',
    'header.title.shop': 'Shop',
    'header.title.chat': 'Chat',
    'header.title.quiz': 'Quiz challenge',
    'header.title.imagine': 'Imagine',
    'header.title.me': 'My profile',
    'header.title.timeline': 'Our story',
    'header.title.fallback': 'My Animal Park',

    'header.pet.waiting': '{name} is waiting',
    'header.pet.fallback': 'Buddy',
    'header.profile.aria': "{name}'s profile",
    'header.home.aria': 'Back home',
    'header.wallet.aria': 'Open wallet',

    'nav.brand.rank': '{emoji} Lv.{level} · {title}',

    'music.title': 'Background music',
    'music.aria': 'Background music',
    'music.tooltip.on': 'Background music: {style}',
    'music.tooltip.off': 'Background music is off',
    'music.power.on': 'Music is on',
    'music.power.off': 'Music is off',
    'music.power.helper.on': 'Tap to turn music off',
    'music.power.helper.off': 'Tap to turn music on',
    'music.foot.scene': 'Scene sounds always stay on',

    'push.aria': 'Turn on iPad chat notifications',
    'push.title.denied': 'System notifications are blocked. Allow them in iPad Settings.',
    'push.title.prompt': 'Turn on chat alerts',
    'push.label.denied': 'Alerts off',
    'push.label.prompt': 'Turn on alerts',

    'chat.fab.unread.aria': '{name} wants to chat',
    'chat.fab.idle.aria': 'Chat with {name}',
    'chat.fab.unread.label': 'Reply',
    'chat.fab.idle.label': 'Chat',

    'update.banner.title': 'A new version is ready',
    'update.banner.body': 'Tap to update to the latest version',
    'update.banner.button': 'Update now',
    'update.banner.dismiss': 'Later',
  },
};

export default shell;
